import React, { useState, useRef, useEffect } from "react";
import {
  Sparkles,
  Loader2,
  Copy,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { GROQ_CONFIG, AI_PROMPTS } from "@/config/groq";
import Modal from "./ui/Modal";

interface AIAssistantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyText: (processedText: string) => void;
  originalText: string;
  placeholder?: string;
}

interface GroqResponse {
  choices: Array<{
    message: {
      content: string;
    };
  }>;
}

export default function AIAssistantModal({
  isOpen,
  onClose,
  onApplyText,
  originalText,
  placeholder = "Descreva o que você gostaria que a IA faça com o texto...",
}: AIAssistantModalProps) {
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [processedText, setProcessedText] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focar no textarea quando o modal abrir
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Resetar estados quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      setPrompt("");
      setProcessedText("");
      setError("");
      setCopied(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!prompt.trim()) {
      toast.error("Por favor, descreva o que você gostaria que a IA faça");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(GROQ_CONFIG.API_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${GROQ_CONFIG.API_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: GROQ_CONFIG.MODEL,
          messages: [
            {
              role: "system",
              content: AI_PROMPTS.TEXT_ASSISTANT,
            },
            {
              role: "user",
              content: `Texto original: ${originalText}\n\nInstrução: ${prompt}\n\nPor favor, melhore o texto seguindo a instrução fornecida. Mantenha o formato HTML se o texto original tiver HTML.`,
            },
          ],
          max_tokens: GROQ_CONFIG.MAX_TOKENS,
          temperature: GROQ_CONFIG.TEMPERATURE,
        }),
      });

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`);
      }

      const data: GroqResponse = await response.json();
      const improvedContent = data.choices[0]?.message?.content || "";

      if (improvedContent) {
        setProcessedText(improvedContent);
        toast.success("Texto processado com sucesso!");
      } else {
        throw new Error("Resposta vazia da API");
      }
    } catch (err: any) {
      console.error("Erro ao chamar API do Groq:", err);
      setError(err.message || "Erro ao processar solicitação");
      toast.error("Erro ao processar solicitação da IA");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(processedText);
      setCopied(true);
      toast.success("Texto copiado para a área de transferência!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Erro ao copiar texto");
    }
  };

  const handleApplyText = () => {
    if (processedText.trim()) {
      onApplyText(processedText);
      onClose();
      toast.success("Texto aplicado com sucesso!");
    }
  };

  const handleClose = () => {
    if (isLoading) return; // Não permitir fechar durante o processamento
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Assistente de IA
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-xs">
              Processe e melhore seu texto com inteligência artificial
            </p>
          </div>
        </div>
      }
      maxWidth="2xl"
      closeOnBackdropClick={!isLoading}
      showCloseButton={true}
    >

      {/* Conteúdo com scroll */}
      <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
        <div className="p-4">
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      {/* Coluna Esquerda - Input */}
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            O que você gostaria que a IA faça?
                          </label>

                          {/* Botões de atalho */}
                          <div className="mb-2 flex flex-wrap gap-1.5">
                            <button
                              type="button"
                              onClick={() =>
                                setPrompt(
                                  "Melhore este texto, torne-o mais claro e profissional"
                                )
                              }
                              className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-800/50 transition-colors border border-purple-200 dark:border-purple-700"
                            >
                              ✨ Melhorar
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setPrompt(
                                  "Corrija erros gramaticais e ortográficos"
                                )
                              }
                              className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors border border-blue-200 dark:border-blue-700"
                            >
                              📝 Corrigir
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setPrompt(
                                  "Torne o texto mais profissional e formal"
                                )
                              }
                              className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors border border-green-200 dark:border-green-700"
                            >
                              💼 Profissional
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setPrompt(
                                  "Simplifique o texto para torná-lo mais claro"
                                )
                              }
                              className="px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-800/50 transition-colors border border-orange-200 dark:border-orange-700"
                            >
                              🔍 Simplificar
                            </button>
                          </div>

                          <form onSubmit={handleSubmit} className="space-y-2">
                            <textarea
                              ref={textareaRef}
                              value={prompt}
                              onChange={(e) => setPrompt(e.target.value)}
                              placeholder={placeholder}
                              className="w-full h-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 transition-all duration-200 resize-none text-sm"
                              disabled={isLoading}
                            />

                            <button
                              type="submit"
                              disabled={isLoading || !prompt.trim()}
                              className="w-full inline-flex items-center justify-center px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium text-sm shadow-lg hover:shadow-xl"
                            >
                              {isLoading ? (
                                <>
                                  <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                  Processando...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-3 h-3 mr-2" />
                                  Processar com IA
                                </>
                              )}
                            </button>
                          </form>
                        </div>

                        {/* Texto Original */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Texto Original
                          </label>
                          <div className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 max-h-20 overflow-y-auto">
                            <div
                              className="text-xs text-gray-700 dark:text-gray-300 prose prose-xs max-w-none"
                              dangerouslySetInnerHTML={{
                                __html:
                                  originalText || "Nenhum texto para melhorar",
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Coluna Direita - Resultado */}
                      <div className="space-y-3">
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Resultado da IA
                        </label>

                        {error && (
                          <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
                            <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                              <AlertCircle className="w-3 h-3" />
                              <span className="text-xs font-medium">
                                Erro: {error}
                              </span>
                            </div>
                          </div>
                        )}

                        {processedText ? (
                          <div className="space-y-2">
                            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700 max-h-40 overflow-y-auto">
                              <div
                                className="text-xs text-gray-800 dark:text-gray-200 prose prose-xs max-w-none"
                                dangerouslySetInnerHTML={{
                                  __html: processedText,
                                }}
                              />
                            </div>

                            <div className="flex gap-2">
                              <button
                                onClick={handleCopyText}
                                className="flex-1 inline-flex items-center justify-center px-2 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium text-sm"
                              >
                                {copied ? (
                                  <>
                                    <CheckCircle className="w-3 h-3 mr-1.5 text-green-600 dark:text-green-400" />
                                    Copiado!
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-3 h-3 mr-1.5" />
                                    Copiar
                                  </>
                                )}
                              </button>

                              <button
                                onClick={handleApplyText}
                                className="flex-1 inline-flex items-center justify-center px-2 py-1.5 bg-green-600 dark:bg-green-500 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors font-medium text-sm"
                              >
                                Aplicar Texto
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="p-6 text-center bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                            <Sparkles className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                            <p className="text-gray-500 dark:text-gray-400 text-xs">
                              {isLoading
                                ? "Processando sua solicitação..."
                                : "O resultado da IA aparecerá aqui após o processamento"}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
        </div>
      </div>
    </Modal>
  );
}
